import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { credentialRequestsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, CreditCard, Eye, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const statusConfig = {
  PENDING_PAYMENT: { label: 'Pendiente de Pago', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  PAYMENT_CONFIRMED: { label: 'Pago Confirmado', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  PENDING_APPROVAL: { label: 'Pendiente Aprobación', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  APPROVED: { label: 'Aprobada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  REJECTED: { label: 'Rechazada', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function CredentialRequestsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? statusFilter : undefined;
      const response = await credentialRequestsAPI.getAll(params);
      setRequests(response.data);
    } catch (error) {
      toast.error('Error al cargar solicitudes de credenciales');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-EC', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-6" data-testid="credential-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Solicitudes de Credenciales</h1>
          <p className="text-slate-500 mt-1">Gestiona las solicitudes de credenciales para representantes</p>
        </div>
        {user?.role === 'PROVIDER' && (
          <Button onClick={() => navigate('/credentials/create')} data-testid="create-credential-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Solicitud
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Estado:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]" data-testid="status-filter">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="PENDING_PAYMENT">Pendiente de Pago</SelectItem>
                  <SelectItem value="PENDING_APPROVAL">Pendiente Aprobación</SelectItem>
                  <SelectItem value="APPROVED">Aprobada</SelectItem>
                  <SelectItem value="REJECTED">Rechazada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">No hay solicitudes de credenciales</p>
            {user?.role === 'PROVIDER' && (
              <Button className="mt-4" onClick={() => navigate('/credentials/create')}>
                <Plus className="w-4 h-4 mr-2" />
                Crear Primera Solicitud
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {requests.map((request) => {
            const status = statusConfig[request.status] || statusConfig.PENDING_PAYMENT;
            const StatusIcon = status.icon;
            
            return (
              <Card 
                key={request.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/credentials/${request.id}`)}
                data-testid={`credential-request-${request.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{request.company_name}</h3>
                          {request.credential_number && (
                            <Badge variant="outline" className="text-xs font-mono">
                              COT-{String(request.credential_number).padStart(4, '0')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">RUC: {request.ruc}</p>
                        {request.provider_company_name && (
                          <p className="text-xs text-slate-400">Empresa: {request.provider_company_name}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-sm text-slate-500">Credenciales</p>
                        <p className="font-semibold text-lg">{request.num_credentials}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-sm text-slate-500">Total</p>
                        <p className="font-semibold text-lg text-primary">{formatCurrency(request.total_price)}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-sm text-slate-500">Fecha</p>
                        <p className="font-medium">{formatDate(request.created_at)}</p>
                      </div>
                      
                      <Badge className={`${status.color} flex items-center gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </Badge>
                      
                      <Button variant="ghost" size="sm" data-testid={`view-request-${request.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
